import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarDays, Users, MapPin } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge, Empty } from "@/components/common";
import ListingCard from "@/components/ListingCard";
import { BookingAmounts } from "@/pages/owner/OwnerDashboard";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, photoUrl, fmtDate, errMsg } from "@/lib/api";
import { toast } from "sonner";

export default function MyBookings() {
  const { t } = useLang();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "bookings";
  const [bookings, setBookings] = useState([]);
  const [favs, setFavs] = useState([]);
  const load = useCallback(() => {
    api.get("/my/bookings").then((r) => setBookings(r.data));
    api.get("/my/favorites").then((r) => setFavs(r.data));
  }, []);
  useEffect(load, [load]);
  const cancel = async (id) => { try { await api.post(`/bookings/${id}/cancel`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const trig = "rounded-full h-10 data-[state=active]:bg-[#142B5C] data-[state=active]:text-white";
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="tenant-area">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF6B00]">{user.company_name || user.name}</p>
      <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B192C]">{t("tenant_title")}</h1>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })} className="mt-8">
        <TabsList className="h-12 grid grid-cols-2 w-full sm:w-80 rounded-full bg-white border border-slate-200 p-1">
          <TabsTrigger value="bookings" className={trig} data-testid="tab-bookings">{t("tab_bookings")}</TabsTrigger>
          <TabsTrigger value="favorites" className={trig} data-testid="tab-favorites">{t("tab_favorites")}</TabsTrigger>
        </TabsList>
        <TabsContent value="bookings" className="mt-6">
          {!bookings.length ? <Empty text={t("no_bookings")} testid="no-bookings" /> : (
            <div className="grid gap-4 md:grid-cols-2" data-testid="bookings-list">
              {bookings.map((b) => (
                <div key={b.booking_id} className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid={`booking-${b.booking_id}`}>
                  <div className="flex gap-4 p-4">
                    <Link to={`/listing/${b.listing_id}`} className="h-24 w-28 shrink-0 overflow-hidden rounded-xl bg-slate-100">{b.photo && <img src={photoUrl(b.photo)} alt="" className="h-full w-full object-cover" />}</Link>
                    <div className="min-w-0 flex-1 space-y-1">
                      <StatusBadge status={b.status} testid={`booking-status-${b.booking_id}`} />
                      <p className="font-semibold text-[#0B192C] truncate">{b.listing_title}</p>
                      <p className="flex items-center gap-1 text-sm text-slate-600"><CalendarDays className="h-4 w-4" />{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</p>
                      <p className="flex items-center gap-1 text-sm text-slate-600"><Users className="h-4 w-4" />{b.guests} · {b.city}</p>
                    </div>
                  </div>
                  <div className="px-4 pb-4 space-y-3">
                    {b.address && <p className="flex items-center gap-1.5 rounded-xl bg-green-50 p-3 text-sm text-green-800" data-testid={`booking-address-${b.booking_id}`}><MapPin className="h-4 w-4" />{t("address_label")}: {b.address}</p>}
                    <BookingAmounts a={b.amounts} />
                    {b.status === "pending" && <button onClick={() => cancel(b.booking_id)} className="h-11 w-full rounded-full border border-slate-300 text-sm font-semibold" data-testid={`cancel-booking-${b.booking_id}`}>{t("cancel_request")}</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="favorites" className="mt-6">
          {!favs.length ? <Empty text={t("no_favorites")} testid="no-favorites" /> : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="favorites-grid">{favs.map((l) => <ListingCard key={l.listing_id} l={l} />)}</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
